import React from 'react';

export interface MoneyProps {
  amountMinor: number | bigint | string | null | undefined;
  currency?: string;
  symbol?: string;
  showSymbol?: boolean;
  colorize?: boolean;
  className?: string;
}

/**
 * Formats integer minor-unit poisha (100 poisha = ৳1.00) into formatted BDT currency.
 * Zero IEEE-754 floating-point errors by treating integer poisha safely.
 */
export function formatBDT(
  amountMinor: number | bigint | string | null | undefined,
  options: { showSymbol?: boolean; symbol?: string } = {}
): string {
  const { showSymbol = true, symbol = '৳' } = options;

  if (amountMinor === null || amountMinor === undefined || amountMinor === '') {
    return showSymbol ? `${symbol}0.00` : '0.00';
  }

  const numericMinor =
    typeof amountMinor === 'bigint'
      ? Number(amountMinor)
      : typeof amountMinor === 'string'
        ? parseInt(amountMinor, 10) || 0
        : Math.round(amountMinor);

  const isNegative = numericMinor < 0;
  const absMinor = Math.abs(numericMinor);

  const integerPart = Math.floor(absMinor / 100);
  const fractionalPart = absMinor % 100;

  // Format integer part with commas (Indian/South Asian or International comma system)
  const formattedInteger = new Intl.NumberFormat('en-BD').format(integerPart);
  const formattedFraction = fractionalPart.toString().padStart(2, '0');

  const formattedAmount = `${formattedInteger}.${formattedFraction}`;
  const prefix = isNegative ? '-' : '';

  return showSymbol
    ? `${prefix}${symbol}${formattedAmount}`
    : `${prefix}${formattedAmount}`;
}

export const Money: React.FC<MoneyProps> = ({
  amountMinor,
  symbol = '৳',
  showSymbol = true,
  colorize = false,
  className = '',
}) => {
  const numeric =
    typeof amountMinor === 'bigint'
      ? Number(amountMinor)
      : typeof amountMinor === 'string'
        ? parseInt(amountMinor, 10) || 0
        : amountMinor || 0;

  const colorClass = colorize
    ? numeric > 0
      ? 'text-emerald-400 font-semibold'
      : numeric < 0
        ? 'text-rose-400 font-semibold'
        : 'text-zinc-400'
    : '';

  return (
    <span className={`inline-flex items-baseline font-mono tabular-nums ${colorClass} ${className}`}>
      {formatBDT(amountMinor, { showSymbol, symbol })}
    </span>
  );
};

export default Money;
